import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Collapse, List, ListItem } from '@mui/material'
import { SynerallInvoice } from '@vae/server/src/modules/ExternalAPIs/Synerall/synerallTypes'
import dayjs from 'dayjs'
import React, { useMemo } from 'react'
import InvoiceCard from './InvoiceCard'

// Props
interface InvoiceListProps {
	invoices: SynerallInvoice[]
	activeTab?: 'paid' | 'unpaid'
}

// Interface for year group data
interface YearGroup {
	year: number
	total: number
	invoices: SynerallInvoice[]
}

export const InvoiceList: React.FC<InvoiceListProps> = ({ invoices, activeTab }) => {
	const [expandedYear, setExpandedYear] = React.useState<number | null>(null)

	// Group invoices by year and convert to array for better manipulation
	const yearGroups = useMemo<YearGroup[]>(() => {
		const groupMap: Record<number, YearGroup> = {}

		// Group invoices by year
		for (const invoice of invoices) {
			const year = dayjs(invoice.IssuedDate).year()

			if (!groupMap[year]) {
				groupMap[year] = { year, total: 0, invoices: [] }
			}

			groupMap[year].invoices.push(invoice)
			groupMap[year].total += invoice.Amount || 0
		}

		// Convert to array and sort by year (descending)
		return Object.values(groupMap).sort((a, b) => b.year - a.year)
	}, [invoices])

	const handleExpandClick = (year: number) => {
		setExpandedYear(expandedYear === year ? null : year)
	}

	// If activeTab is unpaid, show invoices directly without year grouping
	if (activeTab === 'unpaid') {
		return (
			<div>
				<List>
					<div id='invoice-list'>
						{invoices.map((invoice, idx) => (
							<ListItem disableGutters key={`${invoice.Id}-${idx}`}>
								<InvoiceCard
									key={`${invoice.Id}-${idx}`}
									late={false}
									paid={invoice.Balance === 0}
									invoice={invoice}
								/>
							</ListItem>
						))}
					</div>
				</List>
			</div>
		)
	}

	return (
		<div>
			{yearGroups.map(({ year, total, invoices: yearInvoices }) => {
				// Sort invoices by date, newest first
				const sortedInvoices = [...yearInvoices].sort(
					(a, b) => dayjs(b.IssuedDate).valueOf() - dayjs(a.IssuedDate).valueOf()
				)

				return (
					<div id={`year-${year}`} key={year}>
						<div
							className='title-container bg-[#f5f5f5] dark:bg-[#031f22] hover group'
							onClick={() => handleExpandClick(year)}
						>
							<span className='year text-brand-primary-100-light dark:text-brand-primary-200-dark'>
								{year}
							</span>
							<div className='flex flex-row items-center justify-between gap-2'>
								<span className='total text-brand-secondary-100-light dark:text-brand-secondary-100-dark'>
									{total.toFixed(2).replace('.', ',')}{' '}
									<span className='text-[#666778] dark:text-[#d2d2d2] text-lg'>€</span>
								</span>
								<div
									className={`text-text-grey-200 dark:text-text-grey-100 p-2 w-fit transition-transform group-hover:scale-[1.2] group-hover:transition-transform ${expandedYear === year ? 'expand-open' : ''}`}
								>
									{expandedYear === year ? <ExpandLessIcon /> : <ExpandMoreIcon />}
								</div>
							</div>
						</div>
						<Collapse in={expandedYear === year} timeout='auto' unmountOnExit>
							<List>
								<div id='invoice-list'>
									{sortedInvoices.map((invoice, idx) => (
										<ListItem disableGutters key={`${invoice.Id}-${idx}`}>
											<InvoiceCard
												key={`${invoice.Id}-${idx}`}
												late={false}
												paid={invoice.Balance === 0}
												invoice={invoice}
											/>
										</ListItem>
									))}
								</div>
							</List>
						</Collapse>
					</div>
				)
			})}
		</div>
	)
}
